import React from 'react'
import ImageGallery from 'react-image-gallery'
import PropTypes from 'prop-types'
import { path, pathOr, isNil, reverse } from 'ramda'
import moment from 'moment'

import ContentBox from '../../../layout/component/ContentBox'
import Column from '../../../bootstrap/component/Column'
import Row from '../../../bootstrap/component/Row'
import Select from '../../../form/component/Select'
import { formatDateTime } from '../../../../util/datetime'
import { BACKEND_URL } from '../../../../util/api'
import { DEFINED_IMAGE_LIST, IMAGE_TYPE_SELECT } from '../../../customer/state/detail/const'
import currentUserHasAccess from '../../../../util/widget-helper'
import { MEDICAL_PHOTO_CHECK_WIDGET } from '../../../process/ui-definition/process-ui'

class MedicalPhotoCheckWidget extends React.Component {
    constructor (props) {
        super(props)
        this.imageGalleryRef = React.createRef()
        this.state = {
            visualizationLoaded: false,
        }
        this.handleVisualizationLoaded = this.handleVisualizationLoaded.bind(this)
    }

    componentDidMount () {
        window.addEventListener('message', this.handleVisualizationLoaded)
        this.props.findImageVersion(this.props.customerId)
        this.props.findOrCreateTreatmentQuotation(this.props.productType)
    }

    componentWillUnmount () {
        window.removeEventListener('message', this.handleVisualizationLoaded)
    }

    handleVisualizationLoaded (e) {
        if (e.data && e.data.visualizationLoaded) {
            this.setState({ visualizationLoaded: true })
            this.sendStepToVisualization(this.props.currentStep)
        }
    }

    renderVersionedImageGallery () {
        const {
            currentStep,
            setCurrentStep,
            imageVersionList,
            selectedImageType,
            rotateCustomerImage,
            setImageTypeMetaData,
            customerId,
            slideIndex,
        } = this.props

        if (!imageVersionList || !imageVersionList[selectedImageType.name]) {
            return null
        }
        const versionImageList = imageVersionList[selectedImageType.name]
        const image = DEFINED_IMAGE_LIST.find(item => item.name === selectedImageType.name)

        versionImageList.sort((a, b) => {
            a = a.lastModified
            b = b.lastModified
            if (!a && !b) {
                return 0
            }
            if (!a && b) {
                return -1
            }
            if (a && !b) {
                return 1
            }
            return moment(a).unix() - moment(b).unix()
        })

        const imageList = versionImageList.map(version => {
            return {
                path: version.path,
                original: BACKEND_URL + version.link,
                lastModified: formatDateTime(version.lastModified),
                fileName: image.name,
                versionId: version.versionId,
                rotate: (version.meta && version.meta.rotate) ? version.meta.rotate : 0,
                originalImageType: version.originalImageType,
                image,
                rotateCustomerImage,
                setImageTypeMetaData,
                customerId,
                imageStep: this.calculateImageStep(version.lastModified),
            }
        })

        const startIndex = isNil(slideIndex) ? versionImageList.length - 1 : slideIndex
        if (currentStep < 0) {
            // set step on the first load
            setCurrentStep(imageList[startIndex].imageStep)
        }

        return (
            <ImageGallery
                ref={i => this.imageGalleryRef = i}
                startIndex={startIndex}
                renderItem={this.renderImage}
                infinite={false}
                items={imageList}
                showThumbnails={false}
                showPlayButton={false}
                showBullets={false}
                showFullscreenButton={false}
                onSlide={ (index) => this.onImageSlide(index) }
                onClick={ () => this.onImageClick() }
            />)
    }


    renderImage (props) {
        const { lastModified, original, rotate, originalImageType, rotateCustomerImage, versionId, imageStep } = props

        return (
            <div>
                <div className="medical-photo-checkin-image">
                    <div className={`photo-checkin-image-container ${ROTATE_CSS[rotate]}`}>
                        <img src={original} alt=""/>
                    </div>
                </div>
                <div className="image-info">
                    <div className="rotate-icon-container"
                        onClick={ (event) => {
                            event.stopPropagation()
                            event.preventDefault()
                            rotateCustomerImage(originalImageType, versionId, 90)
                        }}
                    >
                        <RotateIcon />
                        <span className="rotate-label">rotate</span>
                    </div>
                    <span>{lastModified}</span>
                    <span className="step-number">Step: {imageStep}</span>
                </div>
            </div>
        )
    }

    calculateImageStep (formattedImageDate) {
        const alignerCheckInList = this.props.customer.treatmentExecutionList.reduce((previous, current) => previous.concat(current.alignerCheckInList), [])
        const checkInDates = alignerCheckInList.map(
            checkIn => {
                const useOldAppValues = (checkIn.newAlignerNumberUpper === null && checkIn.newAlignerNumberLower === null)

                const alignerNumber = useOldAppValues
                    ? Math.max(checkIn.alignerNumberUpper, checkIn.alignerNumberLower)
                    : Math.max(checkIn.newAlignerNumberUpper, checkIn.newAlignerNumberLower)

                const alignerChangeDate = pathOr(moment.now(), ['metaData', 'alignerChangeDate'], checkIn)
                const checkInDate = useOldAppValues
                    ? moment(alignerChangeDate, 'DD.MM.YYYY').format('YYYY-MM-DDTHH:mm:ss.SSS')
                    : alignerChangeDate

                return {
                    checkInDate,
                    alignerNumber,
                }
            })
        const imageDate = new Date(formattedImageDate)
        const imageStep = reverse(checkInDates).find(item => new Date(item.checkInDate) < imageDate)

        return imageStep ? imageStep.alignerNumber : 0
    }

    onSelectImageType (value) {
        const { currentStep, imageVersionList, setSelectedImageType, setSlideIndex } = this.props

        setSelectedImageType(value)
        const versionImageList = imageVersionList[value]
        // we keep the current step status after selecting a new image type on the dropdown
        // if no photo is found for that specific step, we show the last one available
        let preselectedImageIndex = versionImageList.length - 1
        for (let i = 0; i < versionImageList.length; i++) {
            const image = versionImageList[i]
            const imgStep = this.calculateImageStep(image.lastModified)
            if (imgStep === currentStep) {
                preselectedImageIndex = i
            }
        }
        const slideIndex = preselectedImageIndex
        setSlideIndex(slideIndex)
        this.setCurrentImageStep(versionImageList, slideIndex)
    }

    onImageSlide (index) {
        const { imageVersionList, selectedImageType, setSlideIndex } = this.props

        const versionImageList = imageVersionList[selectedImageType.name]
        setSlideIndex(index)
        this.setCurrentImageStep(versionImageList, index)
    }

    setCurrentImageStep (versionImageList, index) {
        const { setCurrentStep } = this.props

        const currentImage = versionImageList[index]
        const step = this.calculateImageStep(currentImage.lastModified)
        setCurrentStep(step)
        this.sendStepToVisualization(step)
    }

    getTreatmentSteps = () => {
        const alignerSystem = pathOr('1-1-2', ['customer', 'treatmentPlanList', '0', 'system'], this.props)
        const numberOfAlignerUpperJaw = pathOr(0, ['customer', 'treatmentPlanList', '0', 'numberOfAlignerUpperJaw'], this.props)
        const numberOfAlignerLowerJaw = pathOr(0, ['customer', 'treatmentPlanList', '0', 'numberOfAlignerLowerJaw'], this.props)
        const maxAligners = Math.max(numberOfAlignerUpperJaw, numberOfAlignerLowerJaw)
        const STEPS_FREQUENCY = alignerSystem.split('-').length
        const TREATMENT_STEPS = []
        for (let i = 0; i <= maxAligners; i++) {
            if (i % STEPS_FREQUENCY === 0) {
                TREATMENT_STEPS.push(i + 1)
            }
        }
        return TREATMENT_STEPS
    }

    sendStepToVisualization = (step) => {
        if (this.state.visualizationLoaded) {
            const TREATMENT_STEPS = this.getTreatmentSteps()
            const visualizationStep = TREATMENT_STEPS.find((s) => s >= step)
            const visualizationSliderValue = TREATMENT_STEPS.indexOf(visualizationStep)
            const iframe = document.getElementById('visualizationIframe')
            iframe.contentWindow.postMessage({ step: visualizationSliderValue }, '*')
        }
    }

    onImageClick () {
        this.imageGalleryRef._toggleFullScreen()
    }

    render () {
        if (!currentUserHasAccess(MEDICAL_PHOTO_CHECK_WIDGET)) {
            return null
        }

        const { currentStep, treatmentQuotation, selectedImageType, imageVersionList } = this.props
        if (!treatmentQuotation) {
            return null
        }
        // disable options with no pictures on the dropdown
        const valueList = IMAGE_TYPE_SELECT.map(item => ({ ...item, disabled: !imageVersionList[item.id] }))

        return (
            <ContentBox title="Medical Photo Check">
                <Row>
                    <Column size={12}>
                        <div className="default-text">You can choose the view from dropdown menu and see the steps on this view by using arrows<br/><br/></div>
                    </Column>
                    <Column size={6}>
                        <i className="fas fa-address-card"></i>
                        <div className="inline-block vertical-align-top">
                            <Select
                                object={selectedImageType}
                                name="name"
                                style={selectStyle}
                                valueList={valueList}
                                onChange={(id, value) => {
                                    this.onSelectImageType(value)
                                }}
                            />
                        </div>
                    </Column>
                </Row>
                <Row>
                    <Column size={6}>
                        <div style={imagesContainerStyle}>
                            { this.renderVersionedImageGallery() }
                        </div>
                    </Column>
                    <Column size={6}>
                        <iframe id="visualizationIframe"
                            style={visualizationStyle}
                            src={`${treatmentQuotation.treatmentLink}&quality=medium`}
                            title="preview"
                            onLoad={() => this.sendStepToVisualization(currentStep)}
                        />
                    </Column>
                </Row>
            </ContentBox>
        )
    }
}

MedicalPhotoCheckWidget.propTypes = {
    treatmentQuotation: PropTypes.object,
    selectedImageType: PropTypes.string,
    setSelectedImageType: PropTypes.func,
    setCurrentStep: PropTypes.func,
    imageVersionList: PropTypes.array,
    customer: PropTypes.object,
    rotateCustomerImage: PropTypes.func,
    setImageTypeMetaData: PropTypes.func,
    customerId: PropTypes.string,
    currentStep: PropTypes.string,
    setSlideIndex: PropTypes.func,
    slideIndex: PropTypes.number,
}

export default MedicalPhotoCheckWidget

const selectStyle = {
    height: 31,
    fontSize: 11,
    marginBottom: 10,
}

const imagesContainerStyle = {
    height: 664,
    overflow: 'hidden',
}

const visualizationStyle = {
    width: '100%',
    height: 600,
    border: '0',
}

const ROTATE_CSS = {
    0: 'grid-rotate-0',
    90: 'grid-rotate-90',
    180: 'grid-rotate-180',
    270: 'grid-rotate-270',
}

const RotateIcon = ({ onClick = () => {} }) => {
    return (
        <svg onClick={onClick} width="24px" height="25px" viewBox="0 0 24 25" >
            <defs>
                <filter x="-32.5%" y="-31.0%" width="165.0%" height="161.9%" filterUnits="objectBoundingBox" id="filter-1">
                    <feOffset dx="0" dy="1" in="SourceAlpha" result="shadowOffsetOuter1"></feOffset>
                    <feGaussianBlur stdDeviation="1" in="shadowOffsetOuter1" result="shadowBlurOuter1"></feGaussianBlur>
                    <feColorMatrix values="0 0 0 0 0.290221632   0 0 0 0 0.290175289   0 0 0 0 0.290237457  0 0 0 0.5 0" type="matrix" in="shadowBlurOuter1" result="shadowMatrixOuter1"></feColorMatrix>
                    <feMerge>
                        <feMergeNode in="shadowMatrixOuter1"></feMergeNode>
                        <feMergeNode in="SourceGraphic"></feMergeNode>
                    </feMerge>
                </filter>
            </defs>
            <g id="Photo-check-in" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                <g id="Medical-Photo-Check-PDF" transform="translate(-408.000000, -692.000000)" fill="#FFFFFF" fillRule="nonzero">
                    <g id="875100-copy" filter="url(#filter-1)" transform="translate(410.000000, 693.000000)">
                        <path d="M17.6846847,0 C17.2600901,0 16.9159009,0.335692469 16.9159009,0.749805439 L16.9159009,3.48876778 C12.957973,-0.248485356 6.64315315,-0.148757322 2.81130631,3.7114205 C1.00545045,5.53064435 -0.00198198198,7.96493096 0.00299949504,10.4970565 C0.00299949504,10.9111695 0.347207207,11.2468619 0.771801802,11.2468619 C1.1963964,11.2468619 1.54054054,10.9111695 1.54054054,10.4970565 C1.54148649,5.94198954 5.32828829,2.25007531 9.99869369,2.25099791 C12.3706306,2.25143724 14.6332883,3.22345607 16.2348198,4.92985983 L12.8291892,6.03728033 C12.4258559,6.1681569 12.2076126,6.59312134 12.3418018,6.98649791 C12.475991,7.37987448 12.9117117,7.59273013 13.315045,7.46185356 L17.9276577,5.96228661 C18.2425225,5.86001046 18.4545495,5.57216109 18.4535173,5.24850628 L18.4535173,0.749761506 C18.4535173,0.335692469 18.1092793,0 17.6846847,0 Z" id="Path"></path>
                        <path d="M19.2222072,9.74729498 C18.7976126,9.74729498 18.4534234,10.0829874 18.4534234,10.4971004 C18.4524775,15.0521674 14.6656757,18.7440816 9.99527027,18.743159 C7.62333333,18.7427197 5.36067568,17.7707008 3.75914414,16.0642971 L7.16477477,14.9568766 C7.56810811,14.826 7.78635135,14.4010356 7.65216216,14.007659 C7.51797297,13.6142824 7.08225225,13.4014268 6.67891892,13.5323033 L2.06630631,15.0318703 C1.75144144,15.1341464 1.53941441,15.4219958 1.54044667,15.7456506 L1.54044667,20.2443954 C1.54044667,20.6585084 1.88463964,20.9942008 2.30923423,20.9942008 C2.73382883,20.9942008 3.07801802,20.6585084 3.07801802,20.2443954 L3.07801802,17.5053891 C7.03594595,21.2426423 13.3508108,21.1429582 17.1826126,17.2827364 C18.9884685,15.4635126 19.9959009,13.0292259 19.9909194,10.4971004 C19.9909194,10.0829874 19.6468018,9.74729498 19.2222072,9.74729498 Z" id="Path"></path>
                    </g>
                </g>
            </g>
        </svg>
    )
}

RotateIcon.propTypes = {
    active: PropTypes.bool,
    onClick: PropTypes.func,
}



// WEBPACK FOOTER //
// ./src/modules/treatment/component/task-widget/MedicalPhotoCheckWidget.js